import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  IAgentConversationByIdPayload,
  IAgentConversationListPayload,
} from '../types/agent-conversation.types';
import { agentConversationService } from '../services/agent-conversation-service';
import { useConfig } from 'hooks/use-config';

export const useGetAgentConversationList = (
  payload: Omit<IAgentConversationListPayload, 'offset' | 'limit'>,
  options?: { enabled?: boolean }
) => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';

  return useInfiniteQuery({
    queryKey: ['agent-conversation-list', payload],
    queryFn: ({ pageParam = 0 }) => {
      return agentConversationService.getAgentConversationList(
        {
          ...payload,
          allow_created_by_filter: true,
          limit: 20,
          offset: pageParam * 1,
        },
        blocksKey
      );
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.length * 20;
      if (totalFetched < lastPage.total_count) {
        return allPages.length;
      }
      return undefined;
    },
    initialPageParam: 0,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!blocksKey && !!payload.agent_id && (options?.enabled ?? true),
  });
};

export const useGetAgentConversationSessionById = (
  payload: IAgentConversationByIdPayload & { enabled?: boolean }
) => {
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const { enabled = true, ...queryPayload } = payload;
  queryPayload.allow_created_by_filter = true;

  return useQuery({
    queryKey: ['agent-conversation', queryPayload],
    queryFn: () => agentConversationService.getAgentConversationSessionById(queryPayload, blocksKey),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: enabled && !!queryPayload.session_id && !!queryPayload.agent_id && !!blocksKey,
  });
};
